/**
 * usePostMessageBridge Hook
 *
 * Handles postMessage communication between parent and child iframes.
 * Provides event listeners, message sending, and request-response handling.
 *
 * @module minting/hooks/usePostMessageBridge
 * @category Minting Framework
 */

import { useEffect, useCallback, useRef, useState } from 'react';
import type {
  PostMessageEvent,
  PostMessageData,
  WalletState,
} from '../types';
// MessageType is a runtime enum, not a type-only symbol: the switch below
// reads MessageType.REQUEST_CONNECT etc. Importing it with `import type`
// erased it, so every branch threw "MessageType is not defined" the moment
// the first message arrived.
import { MessageType } from '../types';
import {
  isPostMessageEvent,
  isMessageType,
  createAppKitInitMessage,
  createAppKitUpdateMessage,
} from '../protocol';

/**
 * Options for the postMessage bridge
 */
export interface UsePostMessageBridgeOptions {
  /** Target origin for messages (default: '*'). Set to specific origin in production */
  targetOrigin?: string;
  /** Called when child sends REQUEST_CONNECT */
  onRequestConnect?: () => Promise<void>;
  /** Called when child sends REQUEST_SIGN_TX */
  onRequestSignTx?: (transaction: any, requestId: string) => Promise<any>;
  /** Called when child sends REQUEST_SIGN_MESSAGE */
  onRequestSignMessage?: (message: any, requestId: string, address?: string) => Promise<any>;
  /** Called when child sends REQUEST_SWITCH_CHAIN */
  onRequestSwitchChain?: (chainId: number, requestId: string) => Promise<void>;
  /** Called when initial AppKit state is sent to child */
  onAppKitStateSent?: (state: WalletState) => void;
  /** Called when any message is received from child */
  onMessage?: (message: PostMessageEvent) => void;
  /** Called when child sends CHILD_READY */
  onChildReady?: () => void;
  /** Called when an error occurs in message handling */
  onError?: (error: Error, message?: PostMessageEvent) => void;
}

/**
 * Return type for usePostMessageBridge
 */
export interface UsePostMessageBridgeReturn {
  /** Send a message to the iframe */
  sendMessage: (message: PostMessageData) => void;
  /** Send current AppKit state to the iframe */
  sendAppKitState: (state: WalletState) => void;
  /** Reference to pending requests (requestId -> resolve function) */
  requestMap: Map<string, (response: any) => void>;
}

/**
 * Hook for managing postMessage communication with an iframe.
 *
 * Features:
 * - Listens for messages from iframe
 * - Handles standard request types (connect, signTx, signMessage, switchChain)
 * - Maintains request/response correlation via requestMap
 * - Provides utilities to send AppKit state updates
 *
 * @param iframeRef - Ref to the iframe HTML element
 * @param options - Configuration callbacks
 * @returns Object with send functions and requestMap
 *
 * @example
 * ```tsx
 * const { sendMessage, sendAppKitState } = usePostMessageBridge(iframeRef, {
 *   targetOrigin: 'https://trusted-dapp.com',
 *   onRequestSignTx: async (tx, requestId) => {
 *     const result = await appKit.signTransaction(tx);
 *     return result;
 *   },
 *   onRequestConnect: async () => {
 *     await appKit.connect();
 *   }
 * });
 * ```
 */
export function usePostMessageBridge(
  iframeRef: React.RefObject<HTMLIFrameElement>,
  options: UsePostMessageBridgeOptions = {}
): UsePostMessageBridgeReturn {
  const {
    targetOrigin = '*',
    onRequestConnect,
    onRequestSignTx,
    onRequestSignMessage,
    onRequestSwitchChain,
    onAppKitStateSent,
    onMessage,
    onChildReady,
    onError,
  } = options;

  // Map to track pending requests: requestId -> resolve function
  const requestMapRef = useRef<Map<string, (response: any) => void>>(new Map());
  const requestMap = requestMapRef.current;

  // Keep track of message listener for cleanup
  const boundHandlerRef = useRef<(event: MessageEvent) => void>();

  /**
   * Send a message to the iframe's contentWindow
   */
  const sendMessage = useCallback((message: PostMessageData) => {
    const iframe = iframeRef.current;
    if (!iframe?.contentWindow) {
      console.warn('[usePostMessageBridge] Cannot send message: iframe not available');
      return;
    }

    try {
      iframe.contentWindow.postMessage(message, targetOrigin);
    } catch (error) {
      console.error('[usePostMessageBridge] Error sending message:', error);
      onError?.(error as Error);
    }
  }, [iframeRef, targetOrigin, onError]);

  /**
   * Send current AppKit state to iframe
   */
  const sendAppKitState = useCallback((state: WalletState) => {
    const message = createAppKitUpdateMessage(state);
    sendMessage(message);
    onAppKitStateSent?.(state);
  }, [sendMessage, onAppKitStateSent]);

  /**
   * Handle incoming message from iframe
   */
  const handleMessage = useCallback((event: MessageEvent) => {
    // Security: validate origin if targetOrigin is set
    if (targetOrigin !== '*' && event.origin !== targetOrigin) {
      console.warn(`[usePostMessageBridge] Ignoring message from untrusted origin: ${event.origin}`);
      return;
    }

    const data = event.data;

    // Filter out messages from browser extensions and dev tools
    // These typically have 'source' or 'target' properties and are not part of our protocol
    if (data && (data.source || data.target)) {
      // Silently ignore - these are external messages not meant for the bridge
      return;
    }

    // Validate message structure
    if (!isPostMessageEvent(data)) {
      // Only log warnings for messages that look like they could be protocol messages
      // but are malformed. Don't spam console for unrelated messages.
      if (data && typeof data === 'object' && !data.source && !data.target) {
        console.warn('[usePostMessageBridge] Received invalid message format:', data);
      }
      return;
    }

    // Call general message handler
    onMessage?.(data);

    const { type, payload } = data;

    try {
      switch (type) {
        case MessageType.REQUEST_CONNECT:
          (async () => {
            try {
              await onRequestConnect?.();
              // After connecting, send updated state
              // Note: Parent AppKit state should be retrieved and sent after connection
            } catch (error) {
              onError?.(error as Error, data);
            }
          })();
          break;

        case MessageType.REQUEST_SIGN_TX:
          (async () => {
            try {
              const result = await onRequestSignTx?.(payload.transaction, payload.requestId);
              // Send response automatically
              const response = {
                type: MessageType.TRANSACTION_RESPONSE,
                payload: {
                  requestId: payload.requestId,
                  success: true,
                  result,
                },
                sender: 'parent',
                timestamp: Date.now(),
              };
              sendMessage(response);

              // Also resolve any await on parent side
              const resolver = requestMap.get(payload.requestId);
              if (resolver) {
                resolver(result);
                requestMap.delete(payload.requestId);
              }
            } catch (error) {
              onError?.(error as Error, data);
              const response = {
                type: MessageType.TRANSACTION_RESPONSE,
                payload: {
                  requestId: payload.requestId,
                  success: false,
                  error: error instanceof Error ? error.message : String(error),
                },
                sender: 'parent',
                timestamp: Date.now(),
              };
              sendMessage(response);

              const resolver = requestMap.get(payload.requestId);
              if (resolver) {
                resolver(null); // or throw? We'll reject separately
                requestMap.delete(payload.requestId);
              }
            }
          })();
          break;

        case MessageType.REQUEST_SIGN_MESSAGE:
          (async () => {
            try {
              const result = await onRequestSignMessage?.(payload.message, payload.requestId, payload.address);
              sendMessage({
                type: MessageType.MESSAGE_RESPONSE,
                payload: { requestId: payload.requestId, success: true, result },
                sender: 'parent',
                timestamp: Date.now(),
              });

              const resolver = requestMap.get(payload.requestId);
              if (resolver) {
                resolver(result);
                requestMap.delete(payload.requestId);
              }
            } catch (error) {
              onError?.(error as Error, data);
              sendMessage({
                type: MessageType.MESSAGE_RESPONSE,
                payload: {
                  requestId: payload.requestId,
                  success: false,
                  error: error instanceof Error ? error.message : String(error),
                },
                sender: 'parent',
                timestamp: Date.now(),
              });

              const resolver = requestMap.get(payload.requestId);
              if (resolver) {
                resolver(null);
                requestMap.delete(payload.requestId);
              }
            }
          })();
          break;

        case MessageType.REQUEST_SWITCH_CHAIN:
          (async () => {
            try {
              await onRequestSwitchChain?.(payload.chainId, payload.requestId);
              sendMessage({
                type: MessageType.CHAIN_SWITCH_RESPONSE,
                payload: { requestId: payload.requestId, success: true },
                sender: 'parent',
                timestamp: Date.now(),
              });

              const resolver = requestMap.get(payload.requestId);
              if (resolver) {
                resolver(true);
                requestMap.delete(payload.requestId);
              }
            } catch (error) {
              onError?.(error as Error, data);
              sendMessage({
                type: MessageType.CHAIN_SWITCH_RESPONSE,
                payload: {
                  requestId: payload.requestId,
                  success: false,
                  error: error instanceof Error ? error.message : String(error),
                },
                sender: 'parent',
                timestamp: Date.now(),
              });

              const resolver = requestMap.get(payload.requestId);
              if (resolver) {
                resolver(false);
                requestMap.delete(payload.requestId);
              }
            }
          })();
          break;

        case MessageType.CHILD_READY:
          onChildReady?.();
          break;

        case MessageType.APPKIT_STATE:
          // Child is requesting current state (handle if needed)
          // Usually the parent automatically sends state, but child can request it
          break;

        default:
          console.warn('[usePostMessageBridge] Unknown message type:', type);
      }
    } catch (error) {
      console.error('[usePostMessageBridge] Error handling message:', error, data);
      onError?.(error as Error, data);
    }
  }, [
    targetOrigin,
    onRequestConnect,
    onRequestSignTx,
    onRequestSignMessage,
    onRequestSwitchChain,
    onChildReady,
    onError,
    sendMessage,
  ]);

  /**
   * Set up message listener on mount
   */
  useEffect(() => {
    boundHandlerRef.current = handleMessage;
    window.addEventListener('message', boundHandlerRef.current);

    return () => {
      if (boundHandlerRef.current) {
        window.removeEventListener('message', boundHandlerRef.current);
      }
    };
  }, [handleMessage]);

  /**
   * Send initial AppKit state when iframe loads (called by parent)
   * This is typically called from the iframe's onLoad handler
   */
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    const handleLoad = () => {
      // AppKit state will be sent by parent component after load
      // This function is just here to ensure proper ordering if needed
    };

    iframe.addEventListener('load', handleLoad);
    return () => iframe.removeEventListener('load', handleLoad);
  }, [iframeRef]);

  return {
    sendMessage,
    sendAppKitState,
    requestMap,
  };
}